import { ProblemDefinition } from './types';

const schemaDDL = `
CREATE TABLE departments (id INTEGER PRIMARY KEY, name TEXT NOT NULL, budget REAL);
CREATE TABLE employees (id INTEGER PRIMARY KEY, first_name TEXT NOT NULL, last_name TEXT NOT NULL, dept_id INTEGER, salary REAL, hire_date TEXT, manager_id INTEGER, FOREIGN KEY(dept_id) REFERENCES departments(id), FOREIGN KEY(manager_id) REFERENCES employees(id));
`;

const seedDML = `
INSERT INTO departments (id, name, budget) VALUES
  (1, 'Engineering', 500000),
  (2, 'Sales', 300000),
  (3, 'Marketing', 200000),
  (4, 'HR', 150000);

INSERT INTO employees (id, first_name, last_name, dept_id, salary, hire_date, manager_id) VALUES
  (1, 'Alice', 'Johnson', 1, 95000, '2020-03-15', NULL),
  (2, 'Bob', 'Smith', 1, 88000, '2021-06-01', 1),
  (3, 'Carol', 'Williams', 2, 72000, '2019-11-20', NULL),
  (4, 'David', 'Brown', 2, 65000, '2022-01-10', 3),
  (5, 'Eva', 'Davis', 3, 78000, '2020-07-22', NULL),
  (6, 'Frank', 'Miller', 1, 92000, '2018-09-05', 1),
  (7, 'Grace', 'Wilson', NULL, 60000, '2023-02-14', NULL),
  (8, 'Henry', 'Taylor', 3, 70000, '2021-04-30', 5),
  (9, 'Ivy', 'Anderson', 4, 67000, '2022-08-18', NULL),
  (10, 'Jack', 'Thomas', NULL, 55000, '2023-05-01', NULL);
`;

export const joinProblems: ProblemDefinition[] = [
 {
  id: 'join-easy-1',
  title: 'Employees with Department Names',
  statement: `Write a query to return the **first name**, **last name**, and **department name** for all employees who belong to a department.\n\nUse an INNER JOIN between \`employees\` and \`departments\`.`,
  schemaDDL,
  seedDML,
  expectedOutput: {
   columns: ['first_name', 'last_name', 'name'],
   values: [
    ['Alice', 'Johnson', 'Engineering'],
    ['Bob', 'Smith', 'Engineering'],
    ['Carol', 'Williams', 'Sales'],
    ['David', 'Brown', 'Sales'],
    ['Eva', 'Davis', 'Marketing'],
    ['Frank', 'Miller', 'Engineering'],
    ['Henry', 'Taylor', 'Marketing'],
    ['Ivy', 'Anderson', 'HR'],
   ],
  },
  comparisonMode: 'order-insensitive',
  difficulty: 'easy',
  topicTags: ['joins'],
  hints: [
   'Use INNER JOIN to combine rows from two tables based on a matching condition.',
   'JOIN employees and departments ON employees.dept_id = departments.id',
  ],
 },
 {
  id: 'join-easy-2',
  title: 'All Employees Including No Department',
  statement: `Write a query to return the **first name**, **last name**, and **department name** for **all** employees.\n\nEmployees without a department should still appear with NULL as their department name. Use a LEFT JOIN.`,
  schemaDDL,
  seedDML,
  expectedOutput: {
   columns: ['first_name', 'last_name', 'name'],
   values: [
    ['Alice', 'Johnson', 'Engineering'],
    ['Bob', 'Smith', 'Engineering'],
    ['Carol', 'Williams', 'Sales'],
    ['David', 'Brown', 'Sales'],
    ['Eva', 'Davis', 'Marketing'],
    ['Frank', 'Miller', 'Engineering'],
    ['Grace', 'Wilson', null],
    ['Henry', 'Taylor', 'Marketing'],
    ['Ivy', 'Anderson', 'HR'],
    ['Jack', 'Thomas', null],
   ],
  },
  comparisonMode: 'order-insensitive',
  difficulty: 'easy',
  topicTags: ['joins'],
  hints: [
   'LEFT JOIN returns all rows from the left table even if there is no match in the right table.',
   'Use: SELECT ... FROM employees LEFT JOIN departments ON employees.dept_id = departments.id',
  ],
 },
 {
  id: 'join-medium-1',
  title: 'Employees and Their Managers',
  statement: `Write a query to return each employee's **first name**, **last name**, and their **manager's first name** (aliased as \`manager_name\`).\n\nEmployees without a manager should still appear with NULL as manager_name. Use a self-join.`,
  schemaDDL,
  seedDML,
  expectedOutput: {
   columns: ['first_name', 'last_name', 'manager_name'],
   values: [
    ['Alice', 'Johnson', null],
    ['Bob', 'Smith', 'Alice'],
    ['Carol', 'Williams', null],
    ['David', 'Brown', 'Carol'],
    ['Eva', 'Davis', null],
    ['Frank', 'Miller', 'Alice'],
    ['Grace', 'Wilson', null],
    ['Henry', 'Taylor', 'Eva'],
    ['Ivy', 'Anderson', null],
    ['Jack', 'Thomas', null],
   ],
  },
  comparisonMode: 'order-insensitive',
  difficulty: 'medium',
  topicTags: ['joins'],
  hints: [
   'A self-join joins a table to itself. Use table aliases to distinguish the two copies.',
   'Example: FROM employees e LEFT JOIN employees m ON e.manager_id = m.id',
   'Select m.first_name AS manager_name to get the manager\'s name.',
  ],
 },
 {
  id: 'join-medium-2',
  title: 'Departments with Employee Counts',
  statement: `Write a query to return each **department name** and the **number of employees** in that department (aliased as \`employee_count\`).\n\nOnly include departments that have at least one employee. Use a JOIN with GROUP BY.`,
  schemaDDL,
  seedDML,
  expectedOutput: {
   columns: ['name', 'employee_count'],
   values: [
    ['Engineering', 3],
    ['Sales', 2],
    ['Marketing', 2],
    ['HR', 1],
   ],
  },
  comparisonMode: 'order-insensitive',
  difficulty: 'medium',
  topicTags: ['joins', 'groupby'],
  hints: [
   'JOIN employees with departments, then GROUP BY department name.',
   'Use COUNT(*) or COUNT(employees.id) to count employees per group.',
   'INNER JOIN will naturally exclude departments with no employees.',
  ],
 },
];
